"use client";

import Image from "next/image";
import Link from "@/components/ui/Link";
import { MessageIcon } from "../ui/message-icon";
import { BOOKMARK_CATEGORIES } from "@/lib/api/public-client";
import { FALLBACK_IMAGE } from '@/lib/constants';
import { getImageUrl, getTimeAgo, getFocusPointStyle, ImageMetadata, BLUR_PLACEHOLDER } from '@/lib/componentutills';

type FieldConfig = {
  hidden?: boolean;
  value?: string;
  customClass?: string;
};

type Props = {
  title?: FieldConfig;
  excerpt ?: FieldConfig;
  timeAgo?: FieldConfig;
  imageSrc?: FieldConfig;
  slug?: string;
  /** Mark as LCP image - preloads for faster rendering */
  priority?: boolean;
  imageMetadata?: ImageMetadata;
};

export const VerticalMediamImageDescNews = ({
  title = { hidden: false, value: "கே.எல்.ராகுலுக்கு வெற்றிகரமான அறுவை சிகிச்சை செய்யப்பட்டது, அவர் திரும்பி வருவதில் உறுதியாக", customClass: "" },
  excerpt = { hidden: false, value: "தாரக் மேத்தா கா ஊல்தா சாஷ்மா படத்தின் தயாரிப்பாளர் அசித் குமார் மோடி சமீப காலமாக பல குற்றச்சாட்டுகளை சந்தித்து வருகிறார். ஜெனிபர் மிஸ்திரி", customClass: "" },
  timeAgo = { hidden: false, value: "May 17, 2023", customClass: "" },
  imageSrc = { hidden: false, value: FALLBACK_IMAGE, customClass: "" },
  slug,
  priority = false,
  imageMetadata,
}: Props) => {
  const titleValue = title?.value || "";
  const excerptValue = excerpt?.value || "";
  const timeAgoValue = getTimeAgo(timeAgo?.value);
  const imageSrcValue = getImageUrl(imageSrc?.value) || FALLBACK_IMAGE;

  const content = (
    <div className="flex flex-col bg-F8F8F8 overflow-hidden p-4 gap-4">
      {/* IMAGE */}
      {!imageSrc?.hidden && (
        <div>
          <div className="relative w-full aspect-[16/9] overflow-hidden">
            <Image
              src={imageSrcValue}
              alt={titleValue}
              fill
              priority={priority}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 33vw, 300px"
              className={`object-cover w-full h-full ${imageSrc?.customClass || ""}`}
              style={getFocusPointStyle(imageMetadata)}
              loading={priority ? "eager" : "lazy"}
              fetchPriority={priority ? "high" : "auto"}
              placeholder="blur"
              blurDataURL={BLUR_PLACEHOLDER}
            />
          </div>
        </div>
      )}

      {/* CONTENT */}
      <div className="flex flex-col gap-3">
        {/* Title */}
        {!title?.hidden && (
          <h3 className={`text-16-tamil-700 leading-[1.4] line-clamp-3 text-1B1B1B h-auto ${title?.customClass || ""}`}>
            {titleValue}
          </h3>
        )}

        {/* Description */}
        {!excerpt?.hidden && (
          <p className={`text-12-tamil-400 text-4D4D4D leading-relaxed line-clamp-4 h-auto ${excerpt?.customClass || ""}`}>
            {excerptValue}
          </p>
        )}

        {/* Footer */}
        <div className="flex justify-between items-center gap-3">
          {!timeAgo?.hidden && (
            <span className={`text-12-tamil-500 ${timeAgo?.customClass || ""}`}>{timeAgoValue}</span>
          )}
          <div className="flex gap-3">
            <MessageIcon
              id="message-icon-3"
              slug={slug}
              category={BOOKMARK_CATEGORIES.NEWS}
              title={titleValue}
              thumbnailUrl={imageSrcValue}
              description={excerptValue}
              articleUrl={slug ? `/${slug}` : undefined}
            />
          </div>
        </div>
      </div>
    </div>
  );

  if (slug) {
    return (
      <Link href={`/${slug}`} className="block hover:bg-gray-100 transition-colors cursor-pointer">
        {content}
      </Link>
    );
  }

  return content;
};